import React, { useState, useEffect, useRef } from 'react';
import DeviceSelector from './components/DeviceSelector';
import SafeguardsAssessment from './components/SafeguardsAssessment';
import ThreatAssessment from './components/ThreatAssessment';
import ResultsDisplay from './components/ResultsDisplay';
import SponsorFooter from './components/SponsorFooter';
import { medicalDevicesDatabase } from './data/medical-device-database';
import Analytics from './utils/analytics';
import WelcomeModal from './components/WelcomeModal';

// Define the informational content component structure here
const HowToAboutContent = () => {
  return (
    <div className="p-4 max-w-4xl mx-auto bg-white rounded shadow mt-4">
      <h1 className="text-2xl font-bold mb-6 text-center">About & How to Use This Tool</h1>

      {/* About the Tool Section */}
      <section className="mb-8 p-4 bg-blue-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-3">About This Risk Assessment Tool</h2>
        <p className="mb-3 text-sm">
          This interactive tool is designed to assist healthcare organizations, IT professionals, compliance officers, 
          and clinical engineering departments in performing cybersecurity risk assessments for medical devices, aligning with 
          HIPAA/HITECH requirements. It provides a structured workflow to evaluate potential vulnerabilities and threats 
          associated with devices that may handle electronic Protected Health Information (ePHI).
        </p>
        <p className="text-sm">
          By systematically analyzing device characteristics, implemented safeguards, and potential threats, this tool helps 
          organizations identify security gaps, prioritize remediation efforts, and document their due diligence for compliance purposes.
        </p>
      </section>

      {/* About the Database Section */}
      <section className="mb-8 p-4 bg-green-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-3">About the Medical Device Database</h2>
        <p className="mb-3 text-sm">
          The tool includes a database of common medical devices across various categories (e.g., diagnostic imaging, patient monitoring, 
          surgical systems, telehealth). For each device, the database provides:
        </p>
        <ul className="list-disc ml-6 mb-3 text-sm space-y-1">
          <li>Typical manufacturer examples</li>
          <li>Common applications</li>
          <li>Default network connectivity status (can be overridden)</li>
          <li>An estimated inherent ePHI risk level (Low, Medium, High)</li>
          <li>An <strong className="font-semibold">estimated</strong> default threat profile based on the STRIDE model (Spoofing, Tampering, Repudiation, Information Disclosure, Denial of Service, Elevation of Privilege).</li>
        </ul>
        <p className="mb-3 text-sm">
          In addition to individual device profiles, the underlying data file (`src/data/medical-device-database.js`) also exports structured information on:
        </p>
        <ul className="list-disc ml-6 mb-3 text-sm space-y-1">
          <li>
            <strong className="font-semibold">Regulatory Matrix:</strong> Summarizes key security requirements from relevant regulations and standards like HIPAA/HITECH, 
            FDA guidance, NIST Cybersecurity Framework, ISO standards, and EU MDR.
          </li>
          <li>
            <strong className="font-semibold">Known Vulnerabilities:</strong> Provides examples of common vulnerability categories (e.g., Authentication Issues, 
            Encryption Flaws, Software Weaknesses) affecting medical devices, including descriptions, typical affected device types, and potential mitigations.
          </li>
        </ul>
        <p className="text-sm mb-3">
          While not directly used in the interactive risk score calculation in this version, this supplementary data offers valuable context for 
          deeper security analysis and understanding regulatory expectations.
        </p>
        <p className="text-sm font-medium text-red-700">
          <strong>Important:</strong> The database information, especially the threat profiles, serves as a <strong className="font-semibold">starting point and estimation</strong> based on typical configurations. 
          You <strong className="font-semibold">must</strong> review and adjust the device information and threat assessment based on your specific device model, configuration, deployment environment, and existing controls.
        </p>
      </section>

      {/* Database Statistics Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">What's in the Database</h2>
        {/* Big stat numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { value: '103', label: 'Total Devices' },
            { value: '9',   label: 'Device Categories' },
            { value: '71',  label: 'High-Risk Devices' },
            { value: '97%', label: 'Network-Connected' },
          ].map(stat => (
            <div key={stat.label} className="rounded-lg p-4 text-center" style={{background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4d8c 100%)'}}>
              <div className="text-3xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-blue-200 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* ePHI Risk Distribution */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">ePHI Risk Distribution (103 Devices)</h3>
          <div className="space-y-2">
            {[
              { label: 'High Risk', count: 71, color: 'bg-red-500', pct: Math.round(71/103*100) },
              { label: 'Medium Risk', count: 26, color: 'bg-amber-400', pct: Math.round(26/103*100) },
              { label: 'Low Risk', count: 6, color: 'bg-green-500', pct: Math.round(6/103*100) },
            ].map(row => (
              <div key={row.label} className="flex items-center gap-3">
                <div className="w-28 text-xs text-gray-600 shrink-0">{row.label}</div>
                <div className="flex-1 bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div className={`${row.color} h-4 rounded-full`} style={{width: `${row.pct}%`}} />
                </div>
                <div className="text-xs text-gray-700 font-medium w-14 text-right shrink-0">{row.count} ({row.pct}%)</div>
              </div>
            ))}
          </div>
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { name: 'Diagnostic Imaging',        count: 11, vendors: ['Philips', 'GE HealthCare', 'Siemens'],       high: 9,  med: 2, low: 0 },
            { name: 'Patient Monitoring',         count: 13, vendors: ['Masimo', 'Philips', 'GE HealthCare'],        high: 8,  med: 4, low: 1 },
            { name: 'Implantable Devices',        count: 10, vendors: ['Medtronic', 'Abbott', 'Boston Scientific'],  high: 9,  med: 1, low: 0 },
            { name: 'Surgical Systems',           count: 11, vendors: ['Intuitive Surgical', 'Stryker', 'Zimmer Biomet'], high: 9, med: 2, low: 0 },
            { name: 'Infusion & Medication',      count: 13, vendors: ['BD', 'ICU Medical', 'Baxter'],               high: 9,  med: 3, low: 1 },
            { name: 'Lab & Diagnostic',           count: 13, vendors: ['Abbott', 'Roche', 'bioMérieux'],             high: 8,  med: 4, low: 1 },
            { name: 'Telehealth & Remote',        count: 12, vendors: ['Teladoc', 'Dräger', 'Masimo'],               high: 9,  med: 2, low: 1 },
            { name: 'EMR & Health IT',            count: 14, vendors: ['Epic', 'Oracle Health', 'Meditech'],         high: 10, med: 4, low: 0 },
            { name: 'AI Clinical Decision',       count: 6,  vendors: ['Nuance', 'Google Health', 'Dexcom'],         high: 0,  med: 4, low: 2 },
          ].map(cat => (
            <div key={cat.name} className="border rounded-lg p-3 bg-white">
              <div className="font-medium text-sm text-gray-800 mb-1">{cat.name}</div>
              <div className="text-xs text-gray-500 mb-2">{cat.count} devices &middot; {cat.vendors.join(', ')}</div>
              <div className="flex gap-1.5">
                {cat.high > 0 && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">{cat.high} High</span>}
                {cat.med > 0  && <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">{cat.med} Med</span>}
                {cat.low > 0  && <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">{cat.low} Low</span>}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">Device counts and vendor examples reflect the 2026 database. Vendor names shown are representative examples; each category contains additional manufacturers.</p>
      </section>

      {/* About the Calculations Section */}
      <section className="mb-8 p-4 bg-yellow-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-3">How Risk Is Calculated</h2>
        <p className="mb-3 text-sm">
          The overall risk score (0-10) is calculated by considering several factors:
        </p>
        <ol className="list-decimal ml-6 mb-3 text-sm space-y-2">
          <li>
            <strong>Threat Assessment:</strong> For each relevant threat identified (based on the default profile or your manual assessment), 
            a score is calculated based on its <strong className="font-semibold">Severity</strong> (potential impact) and <strong className="font-semibold">Likelihood</strong> (probability of occurrence). 
            The sum of these individual threat scores contributes to the base risk.
          </li>
          <li>
            <strong>Device Risk Factors:</strong> The base threat score is adjusted based on inherent device characteristics. Devices that are 
            <strong className="font-semibold">network-connected</strong> or have a higher <strong className="font-semibold">ePHI risk level</strong> (Medium or High) will see an increase 
            in their calculated risk score, reflecting their greater potential exposure or impact.
          </li>
          <li>
            <strong>Safeguards Mitigation:</strong> Your assessment of implemented Administrative, Physical, and Technical safeguards generates an 
            <strong className="font-semibold">Overall Safeguards Score</strong>. This score acts as a mitigating factor, <strong className="font-semibold">reducing</strong> the calculated risk score. 
            Stronger safeguards lead to a lower final risk score.
          </li>
        </ol>
        <p className="text-sm">
          The final score is normalized and clamped to a 0-10 scale, categorized into Low, Medium, High, or Critical risk levels to aid in prioritization.
        </p>
      </section>

      {/* How To Use Section (Moved from App.js) */}
      <section className="mb-8 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-3">How To Use This Tool - Step-by-Step</h2>
        <h3 className="font-medium text-gray-700 mb-2">Assessment Process</h3>
        <ol className="list-decimal ml-5 text-sm space-y-3">
          <li>
            <strong>Device Selection:</strong> Choose your assessment method.
            <ul className="list-disc ml-5 mt-1 text-xs space-y-1">
              <li>
                <strong className="font-semibold">Select from Database:</strong> Use the search and filter options to find your device. Selecting a device pre-populates 
                information in subsequent steps. Click on a device in the list to select it and automatically proceed.
              </li>
              <li>
                <strong className="font-semibold">Custom Device Assessment:</strong> Click the "Start Custom Assessment" button if your device is not in the database 
                or if you prefer to enter all information manually.
              </li>
            </ul>
          </li>
          <li>
            <strong>Device Information:</strong> If you selected from the database, review the pre-populated information (Device Name, Manufacturer, 
            Network Connected, Contains ePHI). Fill in or correct any details like Device ID, Model, Software Version, Location, and Responsible Owner. 
            If starting custom, fill in all relevant fields.
          </li>
          <li>
            <strong>Safeguards Assessment:</strong> Answer the questionnaire regarding the Administrative, Physical, and Technical safeguards 
            implemented for this specific device or device type. Your answers determine the mitigation score.
          </li>
          <li>
            <strong>Threat Assessment:</strong> Evaluate potential threats using the STRIDE categories. If you selected a database device, review the 
            pre-filled severity scores. Adjust the <strong className="font-semibold">Severity</strong> and estimate the <strong className="font-semibold">Likelihood</strong> for each threat based on your environment 
            and the safeguards in place. Check the 'Present' box only for threats you consider relevant and plausible.
          </li>
          <li>
            <strong>Results:</strong> Review the calculated Overall Risk Score, Risk Level, and detailed breakdowns of the safeguard and threat assessments. 
            Use the charts and recommendations to understand the risk posture and plan remediation actions.
          </li>
        </ol>
      </section>
      
      {/* Best Practices Section (Moved from App.js) */}
      <section className="mb-8 p-4 border rounded-lg">
          <h3 className="font-medium text-gray-700 mb-2">Best Practices for Risk Assessment</h3>
          <ul className="list-disc ml-5 text-sm space-y-1">
            <li>Assess each unique type of medical device that processes, stores, or transmits ePHI, considering variations in configuration or deployment.</li>
            <li>Involve relevant stakeholders (IT Security, Clinical Engineering, Compliance, Device Users) in the assessment process.</li>
            <li>Thoroughly document your findings, rationale for threat/likelihood scores, and any planned remediation actions.</li>
            <li>Re-assess devices after significant changes (e.g., software updates, network changes, new integrations) or implementation of new controls.</li>
            <li>Conduct periodic reviews of assessments (at least annually or as defined by your organizational policy).</li>
            <li>Use the assessment results to inform your overall risk management program and prioritize security efforts.</li>
          </ul>
      </section>

      {/* Limitations Section (Moved from App.js) */}
      <section className="p-4 bg-gray-100 rounded-lg mb-8">
          <h3 className="font-medium text-gray-700 mb-2">Limitations & Disclaimer</h3>
          <ul className="list-disc ml-5 text-sm space-y-1 mb-3">
            <li>This tool provides a structured methodology and starting point but <strong className="font-semibold">does not guarantee HIPAA compliance</strong>.</li>
            <li>Risk scores are estimations based on the information provided and the tool's calculation logic.</li>
            <li>Real-world risk may vary based on factors not captured in this assessment.</li>
            <li>Organizations should consult with legal counsel and qualified security experts to ensure comprehensive HIPAA compliance.</li>
            <li>Regulatory requirements and cybersecurity threats evolve; always verify current HIPAA/HITECH rules and threat intelligence.</li>
          </ul>
          <p className="text-sm">
            For the latest official information on HIPAA Security Rule requirements, please visit the
            <a href="https://www.hhs.gov/hipaa/for-professionals/security/index.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
              HHS Office for Civil Rights website
            </a>.
          </p>
      </section>

      {/* Regulatory Sources & References */}
      <section className="mb-8 p-4 border rounded-lg">
        <h2 className="text-xl font-semibold mb-1">Regulatory Sources & References</h2>
        <p className="text-xs text-gray-500 mb-4">The risk methodology, regulatory matrix, threat data, and 2026 compliance deadlines in this tool are grounded in the following primary and secondary sources. Last reviewed April 2026.</p>

        <div className="mb-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Official Regulatory Sources</h3>
          <div className="space-y-2">
            {[
              {
                title: 'HIPAA Security Rule — HHS Office for Civil Rights',
                url: 'https://www.hhs.gov/hipaa/for-professionals/security/index.html',
                desc: '45 CFR §164.308, §164.310, §164.312 — the primary source for all safeguard questions and citations in this tool.'
              },
              {
                title: 'HIPAA Security Rule NPRM — Federal Register (Jan 6, 2025)',
                url: 'https://www.federalregister.gov/documents/2025/01/06/2024-30983/hipaa-security-rule-to-strengthen-the-cybersecurity-of-electronic-protected-health-information',
                desc: 'Full text of the proposed rule. Final rule expected May 2026 with a 180-day compliance window (November 2026 deadline).'
              },
              {
                title: 'FDA Cybersecurity in Medical Devices — Digital Health Center of Excellence',
                url: 'https://www.fda.gov/medical-devices/digital-health-center-excellence/cybersecurity',
                desc: 'Hub for all FDA cybersecurity guidance including the June 2025 final guidance and February 2026 QMSR-aligned revision.'
              },
            ].map(s => (
              <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 rounded-lg border border-blue-100 bg-blue-50 hover:bg-blue-100 transition-colors group">
                <div className="flex-shrink-0 mt-0.5 w-4 h-4 text-blue-500">
                  <svg viewBox="0 0 16 16" fill="currentColor"><path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm.75 12h-1.5V7h1.5v5zm0-6.5h-1.5v-1.5h1.5v1.5z"/></svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800 group-hover:underline">{s.title}</p>
                  <p className="text-xs text-blue-600 mt-0.5">{s.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="mb-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">2026 HIPAA Final Rule — Research Sources</h3>
          <div className="space-y-2">
            {[
              {
                title: 'HIPAA Security Rule: Still on Track for Finalization — Alston & Bird (Nov 2025)',
                url: 'https://www.alston.com/en/insights/publications/2025/11/hipaa-security-rule-overhaul',
                desc: 'Legal analysis confirming OCR regulatory agenda target of May 2026 for final rule publication.'
              },
              {
                title: '2026 HIPAA Security Rule Changes: What Hospitals Must Do — Medcurity',
                url: 'https://medcurity.com/hipaa-security-rule-changes-hospitals-2026/',
                desc: 'Detailed breakdown of mandatory MFA, AES-256 encryption, quarterly scanning, and November 2026 compliance deadline.'
              },
              {
                title: '2026 HIPAA Rule: Mandatory MFA for ePHI Access — AccountableHQ',
                url: 'https://www.accountablehq.com/post/2026-hipaa-rule-mandatory-mfa-for-ephi-access-requirements-and-compliance-guide',
                desc: 'MFA requirement analysis: what systems are in scope, remote vs. onsite requirements, and implementation guidance.'
              },
              {
                title: 'HIPAA Updates and Changes in 2026 — HIPAA Journal',
                url: 'https://www.hipaajournal.com/hipaa-updates-hipaa-changes/',
                desc: 'Running summary of 2026 HIPAA regulatory activity including OCR enforcement actions and rule finalization status.'
              },
              {
                title: 'Critical HIPAA Updates for 2026 — Corsica Technologies',
                url: 'https://corsicatech.com/blog/hipaa-updates-security-rules/',
                desc: 'Practical implementation guidance for encryption standards (AES-256, TLS 1.2+) and BA agreement updates.'
              },
            ].map(s => (
              <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors group">
                <div className="flex-shrink-0 mt-0.5 text-gray-400">
                  <svg viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4"><path d="M6 1a1 1 0 000 2h4.586L1.293 12.293a1 1 0 101.414 1.414L12 4.414V9a1 1 0 102 0V2a1 1 0 00-1-1H6z"/></svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700 group-hover:underline">{s.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">2026 FDA QMSR & Cybersecurity Guidance — Research Sources</h3>
          <div className="space-y-2">
            {[
              {
                title: 'FDA Issues Revised Cybersecurity Premarket Guidance to Align with QMSR — DLA Piper (Feb 2026)',
                url: 'https://www.dlapiper.com/en-us/insights/publications/2026/02/fda-issues-revised-cybersecurity-premarket-submission-guidance',
                desc: 'Legal analysis of the February 9, 2026 FDA guidance revision: QMSR alignment, ISO 13485 clause mapping, no new technical requirements.'
              },
              {
                title: 'FDA Reissues Cybersecurity Guidance to Align with QMSR — RAPS (Feb 2026)',
                url: 'https://www.raps.org/news-and-articles/news-articles/2026/2/fda-reissues-cybersecurity-guidance-to-align-with',
                desc: 'Regulatory affairs perspective on the QMSR transition and its implications for medical device cybersecurity documentation.'
              },
              {
                title: 'FDA Cybersecurity QMSR Guide — MavenRS',
                url: 'https://www.mavenrs.com/FDA-Premarket-Cybersecurity-Guidance-2026-QMSR-ISO-13485--SPDF-Requirements-for-Medical-Devices',
                desc: 'Technical guide covering SPDF requirements, ISO 13485 integration points (Clauses 7.1, 7.3.7, 8.4, 8.5), and SBOM obligations under QMSR.'
              },
              {
                title: "FDA's 2026 Cybersecurity Guidance Introduces Stricter Rules for MedTech — Medical Buyer",
                url: 'https://medicalbuyer.co.in/fdas-2026-cybersecurity-guidance-introduces-stricter-rules-for-medtech/',
                desc: 'Overview of QMSR effective date (February 2, 2026), SPDF scope, and post-market surveillance obligations for device manufacturers.'
              },
            ].map(s => (
              <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer"
                className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors group">
                <div className="flex-shrink-0 mt-0.5 text-gray-400">
                  <svg viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4"><path d="M6 1a1 1 0 000 2h4.586L1.293 12.293a1 1 0 101.414 1.414L12 4.414V9a1 1 0 102 0V2a1 1 0 00-1-1H6z"/></svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 group-hover:text-blue-700 group-hover:underline">{s.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

// Renamed from IntegratedRiskCalculator to App
const SAVE_KEY = 'drh_assessment_v1';

function App() {
  const [activeTab, setActiveTab] = useState('device-selection');
  const [viewMode, setViewMode] = useState('tool'); // 'tool' or 'info'
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const saveTimerRef = useRef(null);
  // ---- CISO Marketplace central gate (members free / credits / one-time guest pass) ----
  const [started, setStarted] = useState(false);
  const csmReportedRef = useRef(false);
  const ensureStarted = async () => {
    if (started) return true;
    const gate = window.csmGate;
    const ok = gate?.requireRun ? await gate.requireRun() : true; // graceful: no gate → allow
    if (ok) setStarted(true);
    return ok;
  };
  // Resume after a guest-pay / sign-in bounce (existing localStorage already restores the assessment).
  useEffect(() => {
    const gate = window.csmGate;
    if (new URLSearchParams(window.location.search).get('gr_session') && gate?.requireRun) {
      gate.requireRun().then((ok) => { if (ok) setStarted(true); });
    }
  }, []);

  // Initialize Google Analytics
  useEffect(() => {
    Analytics.initGA();
    Analytics.trackPageView(window.location.pathname + window.location.search);
  }, []);

  // Load saved assessment on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SAVE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.customDeviceInfo?.deviceName) {
          setShowResumeBanner(true);
        }
      }
    } catch (e) { /* ignore corrupt data */ }
  }, []);

  // Track tab changes as virtual page views
  useEffect(() => {
    const page = `/${activeTab}`;
    Analytics.trackPageView(page);
  }, [activeTab]);

  // Track view mode changes
  useEffect(() => {
    Analytics.trackEvent('Navigation', 'View Mode Change', viewMode);
  }, [viewMode]);
  
  // Device information
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [customDeviceInfo, setCustomDeviceInfo] = useState({
    deviceId: '',
    deviceName: '',
    manufacturer: '',
    model: '',
    softwareVersion: '',
    networkConnected: false,
    containsEphi: false,
    location: '',
    responsibleOwner: ''
  });
  
  // Safeguards scores
  const [adminScore, setAdminScore] = useState(0);
  const [physicalScore, setPhysicalScore] = useState(0);
  const [technicalScore, setTechnicalScore] = useState(0);
  const [overallSafeguardsScore, setOverallSafeguardsScore] = useState(0);
  
  // Threat assessment
  const [threats, setThreats] = useState([
    { 
      name: 'Spoofing', 
      description: 'Can someone impersonate an authorized user on this device?',
      present: false,
      severity: 1, 
      likelihood: 1, 
      score: 1 
    },
    { 
      name: 'Tampering', 
      description: 'Can data be maliciously modified on this device?',
      present: false,
      severity: 1, 
      likelihood: 1, 
      score: 1 
    },
    { 
      name: 'Repudiation', 
      description: 'Can users deny taking an action on this device?',
      present: false,
      severity: 1, 
      likelihood: 1, 
      score: 1 
    },
    { 
      name: 'Information Disclosure', 
      description: 'Can unauthorized users access confidential data?',
      present: false,
      severity: 1, 
      likelihood: 1, 
      score: 1 
    },
    { 
      name: 'Denial of Service', 
      description: 'Can the device be made unavailable to legitimate users?',
      present: false,
      severity: 1, 
      likelihood: 1, 
      score: 1 
    },
    { 
      name: 'Elevation of Privilege', 
      description: 'Can users gain unauthorized privileges on this device?',
      present: false,
      severity: 1, 
      likelihood: 1, 
      score: 1 
    }
  ]);
  
  // Risk assessment results
  const [overallRiskScore, setOverallRiskScore] = useState(0);
  const [riskLevel, setRiskLevel] = useState('');
  const [riskColor, setRiskColor] = useState('');
  const [selectedDeviceCategoryId, setSelectedDeviceCategoryId] = useState(null);
  
  // Administrative safeguards questionnaire
  const [adminAnswers, setAdminAnswers] = useState({
    securityRiskProcess: null,
    riskManagementPlan: null,
    securityIncidentDocumentation: null,
    authorizationProcedures: null,
    accessTerminationProcess: null,
    accessAuthorizationProcess: null,
    roleBasedAccess: null,
    staffTraining: null,
    malwareProtection: null,
    dataBackupPlan: null,
    disasterRecoveryPlan: null
  });
  
  // Physical safeguards questionnaire
  const [physicalAnswers, setPhysicalAnswers] = useState({
    physicalAccessControls: null,
    facilitySecurity: null,
    securityMaintenance: null,
    workstationLocation: null,
    workstationSpecifications: null,
    disposalProcedures: null,
    mediaTracking: null,
    backupCopies: null
  });
  
  // Technical safeguards questionnaire
  const [technicalAnswers, setTechnicalAnswers] = useState({
    uniqueUserIdentification: null,
    emergencyAccessProcedures: null,
    automaticLogoff: null,
    dataEncryption: null,
    auditControls: null,
    integrityControls: null,
    transmissionEncryption: null,
    transmissionIntegrityControls: null
  });
  
  // Initial state for custom device info
  const initialCustomDeviceInfo = {
    deviceId: '',
    deviceName: '',
    manufacturer: '',
    model: '',
    softwareVersion: '',
    networkConnected: false,
    containsEphi: false,
    location: '',
    responsibleOwner: ''
  };
  
  // Handle device selection and prefill threat profiles
  const handleDeviceSelect = async (device) => {
    if (!(await ensureStarted())) return; // gate up front; gate.js shows the unlock overlay if not entitled
    setSelectedDevice(device);

    // Find and store the category ID
    let categoryId = null;
    for (const category of medicalDevicesDatabase.categories) {
      if (category.devices.some(d => d.id === device.id)) {
        categoryId = category.id;
        break;
      }
    }
    setSelectedDeviceCategoryId(categoryId);

    // Track device selection event
    Analytics.trackDeviceSelection(device.name, categoryId);

    // Pre-fill custom device info
    setCustomDeviceInfo({
      ...customDeviceInfo,
      deviceName: device.name,
      manufacturer: device.manufacturer,
      networkConnected: device.networkConnected,
      containsEphi: device.ephiRiskLevel !== 'low'
    });
    
    // Pre-fill threats based on device threat profile
    if (device.threatProfiles) {
      const updatedThreats = threats.map(threat => {
        const threatKey = threat.name.toLowerCase().replace(/\s+/g, '');
        const profileScore = device.threatProfiles[threatKey] || 1;
        
        return {
          ...threat,
          present: profileScore > 1,
          severity: profileScore,
          likelihood: 3, // Default to medium likelihood
          score: profileScore * 3
        };
      });
      
      setThreats(updatedThreats);
    }
    
    // Move to next tab
    setActiveTab('device-info');
    // Track assessment step
    Analytics.trackAssessmentStep(1, 'Device Selection');
  };
  
  // Handle starting a custom assessment
  const handleStartCustomAssessment = async () => {
    if (!(await ensureStarted())) return; // gate up front
    setSelectedDevice(null); // Clear any previously selected DB device
    setCustomDeviceInfo(initialCustomDeviceInfo); // Reset custom info fields
    // Reset threats to default? Optional, depends on desired behavior.
    // For now, let's assume threats are handled separately or pre-filled later.
    setActiveTab('device-info'); // Navigate to the device info tab
  };

  // Resume a saved assessment from localStorage
  const handleResumeAssessment = () => {
    try {
      const saved = localStorage.getItem(SAVE_KEY);
      if (!saved) return;
      const data = JSON.parse(saved);
      if (data.customDeviceInfo) setCustomDeviceInfo(data.customDeviceInfo);
      if (data.selectedDevice) setSelectedDevice(data.selectedDevice);
      if (data.selectedDeviceCategoryId) setSelectedDeviceCategoryId(data.selectedDeviceCategoryId);
      if (data.adminAnswers) setAdminAnswers(data.adminAnswers);
      if (data.physicalAnswers) setPhysicalAnswers(data.physicalAnswers);
      if (data.technicalAnswers) setTechnicalAnswers(data.technicalAnswers);
      if (data.threats) setThreats(data.threats);
      if (data.activeTab) setActiveTab(data.activeTab);
    } catch (e) { /* ignore */ }
    setShowResumeBanner(false);
  };

  // Handle custom device info changes
  const handleDeviceInfoChange = (field, value) => {
    setCustomDeviceInfo({
      ...customDeviceInfo,
      [field]: value
    });
  };
  
  // Handle admin answer changes
  const handleAdminAnswerChange = (question, value) => {
    setAdminAnswers({
      ...adminAnswers,
      [question]: value
    });
  };
  
  // Handle physical answer changes
  const handlePhysicalAnswerChange = (question, value) => {
    setPhysicalAnswers({
      ...physicalAnswers,
      [question]: value
    });
  };
  
  // Handle technical answer changes
  const handleTechnicalAnswerChange = (question, value) => {
    setTechnicalAnswers({
      ...technicalAnswers,
      [question]: value
    });
  };
  
  // Handle threat changes
  const handleThreatChange = (index, field, value) => {
    const newThreats = [...threats];
    newThreats[index][field] = field === 'present' ? value : Number(value);
    if (field === 'severity' || field === 'likelihood') {
      newThreats[index].score = newThreats[index].severity * newThreats[index].likelihood;
    }
    setThreats(newThreats);
  };
  
  // Save assessment to localStorage (debounced 600ms)
  // Placed here — after all useState declarations — to avoid temporal dead zone errors
  useEffect(() => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      try {
        if (customDeviceInfo.deviceName || selectedDevice) {
          localStorage.setItem(SAVE_KEY, JSON.stringify({
            activeTab,
            customDeviceInfo,
            selectedDevice,
            selectedDeviceCategoryId,
            adminAnswers,
            physicalAnswers,
            technicalAnswers,
            threats,
          }));
        }
      } catch (e) { /* ignore quota errors */ }
    }, 600);
  }, [activeTab, customDeviceInfo, selectedDevice, selectedDeviceCategoryId, adminAnswers, physicalAnswers, technicalAnswers, threats]);

  // Save the report to the member portal once results are computed (logged-in only; fire once per result).
  useEffect(() => {
    const gate = window.csmGate;
    if (parseFloat(overallRiskScore) > 0 && !csmReportedRef.current && gate?.isLoggedIn?.() && gate.saveReport) {
      csmReportedRef.current = true;
      gate.saveReport({
        title: 'Device Risk Assessment',
        summary: `Device risk posture — score ${overallRiskScore}/10`,
        data: { device: selectedDevice?.name || customDeviceInfo?.deviceName, overallRiskScore, overallSafeguardsScore, threats },
      });
    }
  }, [overallRiskScore, selectedDevice, customDeviceInfo, overallSafeguardsScore, threats]);

  // Calculate administrative score
  useEffect(() => {
    const questions = Object.keys(adminAnswers);
    let totalPossible = questions.length;
    let totalScore = 0;

    questions.forEach(q => {
      if (adminAnswers[q] !== null && adminAnswers[q] !== -1) {
        totalScore += adminAnswers[q];
      } else {
        totalPossible--;
      }
    });
    
    const calculatedScore = totalPossible > 0 ? (totalScore / totalPossible) * 10 : 0;
    setAdminScore(calculatedScore);
  }, [adminAnswers]);
  
  // Calculate physical score
  useEffect(() => {
    const questions = Object.keys(physicalAnswers);
    let totalPossible = questions.length;
    let totalScore = 0;

    questions.forEach(q => {
      if (physicalAnswers[q] !== null && physicalAnswers[q] !== -1) {
        totalScore += physicalAnswers[q];
      } else {
        totalPossible--;
      }
    });
    
    const calculatedScore = totalPossible > 0 ? (totalScore / totalPossible) * 10 : 0;
    setPhysicalScore(calculatedScore);
  }, [physicalAnswers]);
  
  // Calculate technical score
  useEffect(() => {
    const questions = Object.keys(technicalAnswers);
    let totalPossible = questions.length;
    let totalScore = 0;

    questions.forEach(q => {
      if (technicalAnswers[q] !== null && technicalAnswers[q] !== -1) {
        totalScore += technicalAnswers[q];
      } else {
        totalPossible--;
      }
    });
    
    const calculatedScore = totalPossible > 0 ? (totalScore / totalPossible) * 10 : 0;
    setTechnicalScore(calculatedScore);
  }, [technicalAnswers]);
  
  // Calculate overall safeguards score
  useEffect(() => {
    const safeguardsScore = (adminScore + physicalScore + technicalScore) / 3;
    setOverallSafeguardsScore(safeguardsScore);
  }, [adminScore, physicalScore, technicalScore]);
  
  // Calculate overall risk score
  useEffect(() => {
    // Using the formula: Risk Score = Σ (Threat Impact × Likelihood) / Total Possible Score × 10
    const relevantThreats = threats.filter(threat => threat.present);
    if (relevantThreats.length === 0) {
      setOverallRiskScore(0);
      setRiskLevel('Low');
      setRiskColor('rgb(40, 167, 69)'); // Green
      return;
    }
    
    // Base risk calculation
    const totalThreatScore = relevantThreats.reduce((sum, threat) => sum + (threat.severity * threat.likelihood), 0);
    const maxPossibleScore = relevantThreats.length * (5 * 5); // Max severity * Max likelihood * Number of relevant threats
    
    // Calculate normalized threat score (0-10 scale)
    let normalizedThreatScore = (totalThreatScore / maxPossibleScore) * 10;
    
    // Apply device-specific risk factors
    if (selectedDevice) {
      // Increase risk for high ePHI risk level devices
      if (selectedDevice.ephiRiskLevel === 'high') {
        normalizedThreatScore *= 1.2; // 20% increase for high-risk devices
      } else if (selectedDevice.ephiRiskLevel === 'medium') {
        normalizedThreatScore *= 1.1; // 10% increase for medium-risk devices
      }
      
      // Increase risk for network-connected devices
      if (selectedDevice.networkConnected) {
        normalizedThreatScore *= 1.1; // 10% increase for network-connected devices
      }
    } else if (customDeviceInfo.networkConnected) {
      normalizedThreatScore *= 1.1; // 10% increase for network-connected devices
    }
    
    // Apply safeguards mitigation factor (safeguards reduce risk)
    if (overallSafeguardsScore > 0) {
      normalizedThreatScore = normalizedThreatScore * (1 - (overallSafeguardsScore / 20)); // Dividing by 20 to reduce impact
    }
    
    // Ensure the score is between 0 and 10
    const clampedScore = Math.max(0, Math.min(10, normalizedThreatScore));
    setOverallRiskScore(clampedScore.toFixed(1));
    
    // Determine risk level based on the score
    if (clampedScore >= 8) {
      setRiskLevel('Critical');
      setRiskColor('rgb(220, 53, 69)'); // Red
    } else if (clampedScore >= 6) {
      setRiskLevel('High');
      setRiskColor('rgb(255, 193, 7)'); // Yellow
    } else if (clampedScore >= 4) {
      setRiskLevel('Medium');
      setRiskColor('rgb(255, 136, 0)'); // Orange
    } else {
      setRiskLevel('Low');
      setRiskColor('rgb(40, 167, 69)'); // Green
    }
    
    // Track risk score when it changes (use freshly computed values to avoid stale closure)
    if (clampedScore > 0) {
      const computedLevel = clampedScore >= 8 ? 'Critical' : clampedScore >= 6 ? 'High' : clampedScore >= 4 ? 'Medium' : 'Low';
      Analytics.trackRiskScore(clampedScore.toFixed(1), computedLevel);
    }
  }, [threats, overallSafeguardsScore, selectedDevice, customDeviceInfo.networkConnected]);
  
  // Data for radar chart
  const radarData = threats
    .filter(threat => threat.present)
    .map(threat => ({
      subject: threat.name,
      score: threat.score,
      fullMark: 25,
    }));
  
  // Data for bar chart
  const barData = [
    { name: 'Administrative', score: parseFloat(adminScore.toFixed(1)) },
    { name: 'Physical', score: parseFloat(physicalScore.toFixed(1)) },
    { name: 'Technical', score: parseFloat(technicalScore.toFixed(1)) }
  ];
  
  // Reset function to start a new assessment
  const handleResetAssessment = () => {
    // Reset device selection
    setSelectedDevice(null);
    setSelectedDeviceCategoryId(null);
    
    // Reset device info
    setCustomDeviceInfo({
      deviceId: '',
      deviceName: '',
      manufacturer: '',
      model: '',
      softwareVersion: '',
      networkConnected: false,
      containsEphi: false,
      location: '',
      responsibleOwner: ''
    });
    
    // Reset safeguards
    setAdminAnswers({
      securityRiskProcess: null,
      riskManagementPlan: null,
      securityIncidentDocumentation: null,
      authorizationProcedures: null,
      accessTerminationProcess: null,
      accessAuthorizationProcess: null,
      roleBasedAccess: null,
      staffTraining: null,
      malwareProtection: null,
      dataBackupPlan: null,
      disasterRecoveryPlan: null
    });
    
    setPhysicalAnswers({
      physicalAccessControls: null,
      facilitySecurity: null,
      securityMaintenance: null,
      workstationLocation: null,
      workstationSpecifications: null,
      disposalProcedures: null,
      mediaTracking: null,
      backupCopies: null
    });
    
    setTechnicalAnswers({
      uniqueUserIdentification: null,
      emergencyAccessProcedures: null,
      automaticLogoff: null,
      dataEncryption: null,
      auditControls: null,
      integrityControls: null,
      transmissionEncryption: null,
      transmissionIntegrityControls: null
    });
    
    // Reset threats
    setThreats([
      { 
        name: 'Spoofing', 
        description: 'Can someone impersonate an authorized user on this device?',
        present: false,
        severity: 1, 
        likelihood: 1, 
        score: 1 
      },
      { 
        name: 'Tampering', 
        description: 'Can data be maliciously modified on this device?',
        present: false,
        severity: 1, 
        likelihood: 1, 
        score: 1 
      },
      { 
        name: 'Repudiation', 
        description: 'Can users deny taking an action on this device?',
        present: false,
        severity: 1, 
        likelihood: 1, 
        score: 1 
      },
      { 
        name: 'Information Disclosure', 
        description: 'Can unauthorized users access confidential data?',
        present: false,
        severity: 1, 
        likelihood: 1, 
        score: 1 
      },
      { 
        name: 'Denial of Service', 
        description: 'Can the device be made unavailable to legitimate users?',
        present: false,
        severity: 1, 
        likelihood: 1, 
        score: 1 
      },
      { 
        name: 'Elevation of Privilege', 
        description: 'Can users gain unauthorized privileges on this device?',
        present: false,
        severity: 1, 
        likelihood: 1, 
        score: 1 
      }
    ]);
    
    // Reset tab
    localStorage.removeItem(SAVE_KEY);
    setShowResumeBanner(false);
    setActiveTab('device-selection');

    // Track reset event
    Analytics.trackEvent('Assessment', 'Reset', 'New Assessment');
  };
  
  // Step completion indicators
  const step1Complete = !!(selectedDevice || customDeviceInfo.deviceName);
  const step2Complete = !!(customDeviceInfo.deviceName && customDeviceInfo.manufacturer);
  const step3Complete = Object.values(adminAnswers).some(v => v !== null) ||
                        Object.values(physicalAnswers).some(v => v !== null) ||
                        Object.values(technicalAnswers).some(v => v !== null);
  const step4Complete = threats.some(t => t.present);
  const step5Complete = parseFloat(overallRiskScore) > 0;

  return (
    <>
      <WelcomeModal onHowItWorks={() => setViewMode('info')} />
      <header className="app-header print-hidden">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-white tracking-tight">DeviceRisk</span>
            <span className="hidden sm:inline text-blue-200 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-800 ml-1">HIPAA Tool</span>
          </div>
          <nav className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('tool')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${viewMode === 'tool' ? 'bg-white text-blue-800' : 'text-blue-100 hover:text-white hover:bg-blue-700'}`}
            >
              Assessment
            </button>
            <button
              onClick={() => setViewMode('info')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${viewMode === 'info' ? 'bg-white text-blue-800' : 'text-blue-100 hover:text-white hover:bg-blue-700'}`}
            >
              How It Works
            </button>
          </nav>
        </div>
      </header>
      <div className="p-4 max-w-6xl mx-auto">

      {viewMode === 'tool' && (
        <>
          {/* Hero section */}
          <div className="rounded-xl overflow-hidden shadow-md mb-6" style={{background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4d8c 100%)'}}>
            {/* Stats bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-blue-700">
              {[
                { value: '103', label: 'Devices Tracked' },
                { value: '9',   label: 'Device Categories' },
                { value: '100+', label: 'Vendor Profiles' },
                { value: '2026', label: 'HIPAA/FDA Updated' },
              ].map(stat => (
                <div key={stat.label} className="px-4 py-4 text-center border-r border-blue-600 last:border-r-0" style={{background: 'rgba(255,255,255,0.07)'}}>
                  <div className="text-2xl font-bold text-white drop-shadow">{stat.value}</div>
                  <div className="text-xs font-medium text-blue-100 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
            {/* Body */}
            <div className="p-6">
              <h2 className="text-xl font-bold text-white mb-3">
                HIPAA Medical Device Cybersecurity Risk Assessment — Updated for 2026
              </h2>
              <p className="text-blue-100 text-sm leading-relaxed mb-3">
                Assess the cybersecurity risk of any medical device in minutes. The <strong className="text-white">HIPAA Security Rule final rule</strong> is expected <strong className="text-white">May 2026</strong> — eliminating "addressable" safeguards and making MFA, AES-256 encryption, quarterly vulnerability scanning, and 24-hour BA breach notification <strong className="text-white">mandatory</strong> with a <strong className="text-white">November 2026 compliance deadline</strong>. The <strong className="text-white">FDA's revised Feb 2026 guidance</strong> embeds cybersecurity into QMSR/ISO 13485 quality systems, with SBOMs and 30-day vulnerability notifications required for all "cyber devices."
              </p>
              <p className="text-blue-100 text-sm leading-relaxed">
                With healthcare ransomware up 30% in 2025 and 53% of networked medical devices carrying at least one known critical vulnerability, structured risk documentation is no longer optional.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm">Why Use This Tool?</h3>
                  <ul className="text-gray-700 text-xs space-y-1.5">
                    <li>• Complete HIPAA-aligned assessment in minutes</li>
                    <li>• Pre-loaded profiles for 103 medical devices</li>
                    <li>• Identify security gaps and prioritize remediation</li>
                    <li>• Document compliance for regulators and auditors</li>
                    <li>• Export detailed PDF and CSV reports (members free; credits / guest pass)</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm">Who Should Use This Tool?</h3>
                  <ul className="text-gray-700 text-xs space-y-1.5">
                    <li>• Healthcare IT and security professionals</li>
                    <li>• Compliance officers and privacy officials</li>
                    <li>• Medical device management teams</li>
                    <li>• Healthcare administrators and risk managers</li>
                    <li>• Biomedical and clinical engineering departments</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Resume saved assessment banner */}
          {showResumeBanner && (
            <div className="mb-4 flex items-center gap-3 p-3 bg-amber-50 border border-amber-300 rounded-lg">
              <span className="text-amber-700 text-sm font-medium">You have an unfinished assessment saved.</span>
              <button
                className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600"
                onClick={handleResumeAssessment}
              >
                Continue
              </button>
              <button
                className="px-3 py-1 text-sm text-amber-600 hover:underline"
                onClick={() => { localStorage.removeItem(SAVE_KEY); setShowResumeBanner(false); }}
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="mb-6 flex border-b overflow-x-auto">
            <button
              className={`py-2 px-4 mr-2 whitespace-nowrap ${activeTab === 'device-selection' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('device-selection')}
            >
              1. Device Selection {step1Complete && <span className="ml-1 text-green-500 font-bold">✓</span>}
            </button>
            <button
              className={`py-2 px-4 mr-2 whitespace-nowrap ${activeTab === 'device-info' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('device-info')}
              disabled={!selectedDevice && !customDeviceInfo.deviceName}
            >
              2. Device Info {step2Complete && <span className="ml-1 text-green-500 font-bold">✓</span>}
            </button>
            <button
              className={`py-2 px-4 mr-2 whitespace-nowrap ${activeTab === 'safeguards' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('safeguards')}
              disabled={!selectedDevice && !customDeviceInfo.deviceName}
            >
              3. Safeguards {step3Complete && <span className="ml-1 text-green-500 font-bold">✓</span>}
            </button>
            <button
              className={`py-2 px-4 mr-2 whitespace-nowrap ${activeTab === 'threats' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('threats')}
              disabled={!selectedDevice && !customDeviceInfo.deviceName}
            >
              4. Threats {step4Complete && <span className="ml-1 text-green-500 font-bold">✓</span>}
            </button>
            <button
              className={`py-2 px-4 whitespace-nowrap ${activeTab === 'results' ? 'border-b-2 border-blue-500 font-medium' : 'text-gray-500'}`}
              onClick={() => setActiveTab('results')}
              disabled={!selectedDevice && !customDeviceInfo.deviceName}
            >
              5. Results {step5Complete && <span className="ml-1 text-green-500 font-bold">✓</span>}
            </button>
          </div>
          
          {activeTab === 'device-selection' && (
            <div>
              <div className="bg-white p-6 rounded shadow mb-8">
                <h2 className="text-xl font-semibold mb-4">Choose Assessment Method</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div 
                    className="border rounded p-4 cursor-pointer hover:bg-blue-50 transition-colors"
                    onClick={handleStartCustomAssessment}
                  >
                    <h3 className="font-medium text-lg mb-2">Custom Device Assessment</h3>
                    <p className="text-sm mb-4">
                      Manually enter information about your medical device and conduct a complete assessment without using our device database.
                    </p>
                    <button className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                      Start Custom Assessment
                    </button>
                  </div>
                  <div className="border rounded p-4">
                    <h3 className="font-medium text-lg mb-2">Select from Device Database</h3>
                    <p className="text-sm mb-4">
                      Choose from our comprehensive database of medical devices to pre-populate risk profiles and streamline your assessment.
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      Select a device from our database below:
                    </p>
                    <DeviceSelector onDeviceSelect={handleDeviceSelect} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'device-info' && (
            <div className="bg-white p-6 rounded shadow mb-8">
              <h2 className="text-xl font-semibold mb-4">Medical Device Information</h2>
              
              {selectedDevice && (
                <div className="mb-4 p-3 bg-blue-50 rounded">
                  <p className="text-sm">
                    <span className="font-medium">Selected from database:</span> {selectedDevice.name} ({selectedDevice.manufacturer})
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    You can customize the information below or proceed with the pre-populated values.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Device ID</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={customDeviceInfo.deviceId} 
                    onChange={(e) => handleDeviceInfoChange('deviceId', e.target.value)}
                    placeholder="e.g., MD-001" 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Device Name</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={customDeviceInfo.deviceName} 
                    onChange={(e) => handleDeviceInfoChange('deviceName', e.target.value)}
                    placeholder="e.g., Infusion Pump" 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Manufacturer</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={customDeviceInfo.manufacturer} 
                    onChange={(e) => handleDeviceInfoChange('manufacturer', e.target.value)}
                    placeholder="e.g., MedTech Inc." 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Model</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={customDeviceInfo.model} 
                    onChange={(e) => handleDeviceInfoChange('model', e.target.value)}
                    placeholder="e.g., IP-2000" 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Software Version</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={customDeviceInfo.softwareVersion} 
                    onChange={(e) => handleDeviceInfoChange('softwareVersion', e.target.value)}
                    placeholder="e.g., v3.2.1" 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Location</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={customDeviceInfo.location} 
                    onChange={(e) => handleDeviceInfoChange('location', e.target.value)}
                    placeholder="e.g., ICU Floor 3" 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Responsible Owner</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded" 
                    value={customDeviceInfo.responsibleOwner} 
                    onChange={(e) => handleDeviceInfoChange('responsibleOwner', e.target.value)}
                    placeholder="e.g., Dr. Smith" 
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="networkConnected" 
                    className="mr-2" 
                    checked={customDeviceInfo.networkConnected} 
                    onChange={(e) => handleDeviceInfoChange('networkConnected', e.target.checked)}
                  />
                  <label htmlFor="networkConnected" className="text-sm font-medium">Network Connected?</label>
                </div>
                
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="containsEphi" 
                    className="mr-2" 
                    checked={customDeviceInfo.containsEphi} 
                    onChange={(e) => handleDeviceInfoChange('containsEphi', e.target.checked)}
                  />
                  <label htmlFor="containsEphi" className="text-sm font-medium">Contains/Processes ePHI?</label>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button 
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() => setActiveTab('safeguards')}
                  disabled={!customDeviceInfo.deviceName}
                >
                  Next: Safeguards Assessment
                </button>
              </div>
            </div>
          )}
          
          {activeTab === 'safeguards' && (
            <SafeguardsAssessment
              adminAnswers={adminAnswers}
              setAdminAnswers={setAdminAnswers}
              physicalAnswers={physicalAnswers}
              setPhysicalAnswers={setPhysicalAnswers}
              technicalAnswers={technicalAnswers}
              setTechnicalAnswers={setTechnicalAnswers}
              adminScore={adminScore}
              physicalScore={physicalScore}
              technicalScore={technicalScore}
              device={selectedDevice}
            />
          )}
          
          {activeTab === 'threats' && (
            <ThreatAssessment
              threats={threats}
              setThreats={setThreats}
              selectedDevice={selectedDevice}
            />
          )}
          
          {activeTab === 'results' && (
            <ResultsDisplay
              deviceInfo={customDeviceInfo}
              adminScore={adminScore}
              physicalScore={physicalScore}
              technicalScore={technicalScore}
              overallSafeguardsScore={overallSafeguardsScore}
              threats={threats}
              overallRiskScore={overallRiskScore}
              riskLevel={riskLevel}
              riskColor={riskColor}
              medicalDevicesDatabase={medicalDevicesDatabase}
              selectedDevice={selectedDevice}
              selectedDeviceCategoryId={selectedDeviceCategoryId}
              onReset={handleResetAssessment}
            />
          )}
        </>
      )}

      {viewMode === 'info' && (
        <HowToAboutContent />
      )}

      {/* Add SponsorFooter at the bottom of the page */}
      <SponsorFooter onHowItWorks={() => setViewMode('info')} />
    </div>
  </>
  );
}

export default App;